import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Now } from 'temporal-luxon';
import { DatePicker, H5, Link, ModalWindow, Spinner, Text, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { usePtsDocumentModal, usePtsFinalDocumentUrl } from './use_pts_document_modal.hook';
import { FileDropZone } from '../file_drop_zone';
const PDF_MAX_FILE_SIZE_MB = 50;
const PDF_ACCEPTED_TYPES = '.pdf';
export const PtsDocumentModal = ({ isOpen, onClose, signedDocId, status, signatureDate, finalDocumentUrl, accountId, isNoPiiSite = false, apiClient, getApiErrorMessage, onComplete, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const today = Now.plainDateISO();
    const [selectedDate, setSelectedDate] = useState(today);
    const [selectedFile, setSelectedFile] = useState(undefined);
    const isViewMode = status === 'complete';
    const { submitPtsDocument, isSubmitting } = usePtsDocumentModal({
        apiClient,
        signedDocId,
        accountId,
        onComplete,
    });
    // The list read may not resolve the File `url`; fetch fresh by id in view mode
    // and fall back to the value threaded from the table row.
    const { finalDocumentUrl: fetchedFinalDocumentUrl } = usePtsFinalDocumentUrl({
        apiClient,
        signedDocId,
        accountId,
        enabled: isViewMode,
    });
    const resolvedFinalDocumentUrl = fetchedFinalDocumentUrl ?? finalDocumentUrl;
    const handleClose = () => {
        setSelectedDate(today);
        setSelectedFile(undefined);
        onClose();
    };
    const handleSubmit = async () => {
        if (!selectedDate) {
            return;
        }
        try {
            await submitPtsDocument({ signatureDate: selectedDate, file: selectedFile });
            addNotification({
                title: '',
                subtitle: selectedFile
                    ? translate('info.pts_document.upload_success')
                    : translate('info.pts_document.complete_success'),
                type: 'success',
            });
            handleClose();
        }
        catch (error) {
            const message = getApiErrorMessage?.(error) ?? translate('error.pts_document.complete');
            addNotification({ title: '', subtitle: message, type: 'error' });
        }
    };
    const savingButton = (_jsx("div", { className: 'flex items-center gap-3', children: _jsx(Spinner, { size: 'md' }) }));
    const primaryActionButton = isViewMode
        ? { label: translate('generic.done'), onClick: handleClose, dataTestId: 'pts-document-done' }
        : {
            label: translate('action.pts_document.complete'),
            onClick: handleSubmit,
            disabled: !selectedDate || isSubmitting,
            content: isSubmitting ? savingButton : undefined,
            dataTestId: 'pts-document-submit',
        };
    const secondaryActionButtons = isViewMode
        ? undefined
        : [{ label: translate('generic.cancel'), onClick: handleClose, disabled: isSubmitting }];
    return (_jsx(ModalWindow, { title: translate('label.pts_document.title'), isOpen: isOpen, closeWindow: handleClose, footerPrimaryActionButton: primaryActionButton, footerSecondaryActionButtons: secondaryActionButtons, width: 'base', dataTestId: 'pts-document-modal', children: _jsx("div", { className: 'flex flex-col gap-6 py-4', children: isViewMode ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(H5, { as: 'h3', className: 'text-sm font-medium', children: translate('label.pts_document.signature_date') }), _jsx(Text, { size: 'sm', className: 'text-text-secondary', dataTestId: 'pts-document-signature-date', children: signatureDate || translate('info.pts_document.no_signature') })] }), resolvedFinalDocumentUrl && (_jsx(Link, { href: resolvedFinalDocumentUrl, target: '_blank', rel: 'noopener noreferrer', "data-testid": 'pts-document-download', children: translate('action.pts_document.download') }))] })) : (_jsxs(_Fragment, { children: [_jsx(Text, { size: 'sm', className: 'text-text-secondary', children: translate('label.pts_document.date_instructions') }), _jsx(DatePicker, { id: 'pts-document-date', label: translate('label.pts_document.signature_date'), value: selectedDate, onChange: (date) => setSelectedDate(date), endDate: today, required: true }), !isNoPiiSite && (_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(H5, { as: 'h3', className: 'text-sm font-medium', children: translate('label.pts_document.upload_label') }), _jsx(FileDropZone, { acceptedFileTypes: PDF_ACCEPTED_TYPES, acceptedExtensions: ['PDF'], maxFiles: 1, maxFileSize: PDF_MAX_FILE_SIZE_MB, description: translate('label.pts_document.upload_notice'), disabled: isSubmitting, onUpload: async (files) => {
                                    setSelectedFile(files[0]);
                                } }), selectedFile && (_jsx(Text, { size: 'sm', className: 'text-text-secondary', dataTestId: 'pts-document-selected-file', children: selectedFile.name }))] }))] })) }) }));
};
